import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { TablePagination, TableSortLabel } from "@mui/material";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
}

type SortDirection = "asc" | "desc";

const hasValue = (value: any) => value !== null && value !== undefined && value !== "";

const ConfigAndMapDetailsTable = ({ tableHeadings, rows }: Props) => {
  const headCells = Object.entries(tableHeadings);
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 10;
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      const comparison =
        typeof aValue === "number" && typeof bValue === "number"
          ? aValue - bValue
          : String(aValue).localeCompare(String(bValue));
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, sortKey, sortDirection]);

  const pagedRows = rows.length > 10
    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;

  return (
    <>
      <TableContainer className="receiptsTable" sx={{ my: 1, width: "50%" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map(([key, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={sortKey === key}
                    direction={sortKey === key ? sortDirection : "asc"}
                    onClick={() => handleSort(key)}
                  >
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row, index) => (
              <React.Fragment key={row.metric ?? index}>
                <TableRow
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                  }}
                >
                  {headCells.map(([key], cellIndex) => (
                    <TableCell key={cellIndex} align="left">
                      {hasValue(row[key]) ? row[key] : ""}
                    </TableCell>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > 10 && (
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
        />
      )}
    </>
  );
};

export default ConfigAndMapDetailsTable;
